//core
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import { Box, useMediaQuery } from "@mui/material";

//styles
import { useStyles } from "./styles";

//constant
import { favoriteFilterKeys, statusOptions } from "../../../../constants";

//hooks
import { usePartners } from "../../../../../../../store/admin/partners/usePartners";
import { useFields } from "../../../../../../../store/common/fields/useFields";
import { useSummaryReport } from "../../../../../../../store/admin/reports/summary_report_v2/useSummaryReport";
import useDidMountEffect from "../../../../../../../customHooks/useDidMountEffect";

//context
import { GlobalContext } from "../../../../../../../context/GlobalContext";

//components
import MuiAutocompleteSelectLazy from "../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import { IFlatFeeFilterDataType } from "../../../../types";
import MuiAutocompleteSelect from "../../../../../../UI/Fields/MuiAutocompleteSelect";
import {getDataIds} from "../../../../../Reports/utils";


interface IVisibleFilterProps {
  filter: IFlatFeeFilterDataType;
  setUpdateFilter: Dispatch<SetStateAction<IFlatFeeFilterDataType>>;
}

const VisibleFilter: React.FC<IVisibleFilterProps> = ({
  filter,
  setUpdateFilter,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

	const { getAffiliates } = usePartners();
	const { getTrackers, getBrands } = useFields();
	const { getCurrencies } = useSummaryReport();

	const context = useContext(GlobalContext);

  //favorite fields state
	const [favAdmFFStatuses, setFavAdmFFStatuses] = useLocalStorage<boolean>(favoriteFilterKeys.statuses, false);
	const [favAdmFFAffiliateIds, setFavAdmFFAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
	const [favAdmFFTrackerIds, setFavAdmFFTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
	const [favAdmFFBrandCodes, setFavAdmFFBrandCodes] = useLocalStorage<boolean>(favoriteFilterKeys.brandCodes, false);
	const [favAdmFFBrandCurrencies, setFavAdmFFBrandCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.brandCurrencies, false);


	useEffect(() => {
    context.setAdmFlatFeeFilter(filter);
  }, [filter]);

	useDidMountEffect(() => {
		setUpdateFilter((prevState) => ({
			...prevState,
			brandCurrencies: [],
		}));
	}, [filter.brandCodes]);

	const fieldAffiliateIdsPayload = () => {
		return {
			chiefIds: null,
			departmentIds: null,
			status: 'approved'
		}
	};

	const fieldTrackerIdsPayload = () => {
		return {
			per_page: 100,
			...(!!filter?.affiliateIds.length ? { affiliateIds: getDataIds(filter.affiliateIds) } : {}),
		}
	};

	const fieldBrandCurrenciesPayload = () => {
		return {
			page: 1,
			search: null,
			perPage: 100,
			...(!!filter?.brandCodes.length ? { brandCodes: getDataIds(filter.brandCodes) } : {}),
		}
	};

	const getBrandCurrenciesWithNull = useCallback(async (payload: any) => {
		const data = await getCurrencies(payload);
		return {
			...data,
			payload: {
			...data.payload,
			list: [{ id: null, value: t(`common.components.filters.without_currency`) }, ...data.payload.list],
			},
		};
	}, [getCurrencies]);

  const favoriteFields = !mobile &&
  (favAdmFFStatuses || favAdmFFAffiliateIds || favAdmFFTrackerIds || favAdmFFBrandCodes || favAdmFFBrandCurrencies)
    ? (
      <Box className={styles.favoriteFields}>
	      {favAdmFFAffiliateIds && (
		      <Box>
			      <MuiAutocompleteSelectLazy
				      keyValue="value"
				      keyId="id"
				      id="affiliateIds"
				      name="affiliateIds"
				      label={t(`admin.flatFee.filter.affiliateIds`)}
				      value={filter.affiliateIds}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      getOptions={getAffiliates}
				      payloadData={fieldAffiliateIdsPayload()}
				      labelWithId={true}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      showLabelCounter={true}
				      showNameInPayload={true}
			      />
		      </Box>
	      )}
	      {favAdmFFTrackerIds && (
		      <Box>
			      <MuiAutocompleteSelectLazy
				      keyValue="value"
				      keyId="id"
				      id="trackerIds"
				      name="trackerIds"
				      label={t(`admin.flatFee.filter.trackerIds`)}
				      value={filter.trackerIds}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      getOptions={getTrackers}
				      payloadData={fieldTrackerIdsPayload()}
				      labelWithId={false}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      showLabelCounter={true}
				      showNameInPayload={true}
			      />
		      </Box>
	      )}
	      {favAdmFFBrandCodes && (<>
		      <Box>
			      <MuiAutocompleteSelectLazy
				      keyValue="value"
				      keyId="id"
				      id="brandCodes"
				      name="brandCodes"
				      label={t("admin.flatFee.filter.brandCodes")}
				      value={filter.brandCodes}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      getOptions={getBrands}
				      payloadData={{}}
				      labelWithId={true}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      showLabelCounter={true}
				      showNameInPayload={true}
			      />
		      </Box>
	      </>)}
	      {favAdmFFBrandCurrencies && (<>
		      <Box>
			      <MuiAutocompleteSelectLazy
				      keyValue="value"
				      keyId="id"
				      id="brandCurrencies"
				      name="brandCurrencies"
				      label={t(`admin.flatFee.filter.brandCurrencies`)}
				      value={filter.brandCurrencies}
				      multiple={true}
				      getOptions={getBrandCurrenciesWithNull}
				      payloadData={fieldBrandCurrenciesPayload()}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      showLabelCounter={true}
			      />
		      </Box>
	      </>)}
	      {favAdmFFStatuses && (<>
		      <Box>
			      <MuiAutocompleteSelect
				      keyValue="value"
				      keyId="id"
				      id="statuses"
				      name="statuses"
				      label={t("admin.flatFee.filter.statuses")}
				      value={filter.statuses}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      staticOptions={statusOptions}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      showLabelCounter={true}
				      prefix="admin.flatFee.form.options.statuses."
			      />
		      </Box>
	      </>)}

      </Box>
    ) : null;

  return (<>
	    {favoriteFields}
  </>);
};

export default VisibleFilter;
